<script setup lang="ts">
import { formatShortestPeriod } from '~/utils/date'
import { getPriceText } from '~/helpers/priceHelper'
import type { HouseDetails } from '~/types/house/types'
import type { Locale, SearchHouse } from 'lc-services/types'
import type { VirtualBaseCarouselSwiperOptions } from '../VirtualBaseCarousel.vue'

const { house, destinationIds } = defineProps<{
  destinationIds: string[]
  house: HouseDetails
}>()
defineEmits<{
  'click-favorite': [
    {
      id: number
      name: string
      iconicCollection?: boolean
      onlineReservation?: boolean
      publicPrice?: number
    },
  ]
}>()

const { locale, t } = useI18n<unknown, Locale>()
const { trackEvent } = useTracking()
const { isDesktopOrTablet, isMobile } = useBreakpoint()
const { currency } = useUser()
const { booking, bookingPeriod } = useHouseInquiryBooking()
const { initSimilarHouse } = useSimilarHouses(house.id)

const swiperOptions = computed<VirtualBaseCarouselSwiperOptions>(() => ({
  breakpoints: {
    768: {
      slidesPerGroup: 3,
      slidesPerView: 3,
      spaceBetween: 16,
    },
  },
  loop: true,
  slidesPerGroup: 1,
  slidesPerView: 1,
  spaceBetween: 8,
}))

const { data: housesSimilar, refresh } = useAsyncData(
  `houses-similar-${house.id}`,
  () =>
    initSimilarHouse({
      booking: booking.value,
      capacity: house.capacity.toString(),
      currency: currency.value,
      destinationIds,
    }),
  { watch: [currency, () => house.id] },
)

const onCrossSellClick = (slideIndex: number, item: SearchHouse) => {
  trackEvent({
    event: 'cross_sell_click',
    position: slideIndex,
    product: {
      house_id: item.id,
      house_name: item.name,
    },
  })
}

watch(bookingPeriod, async (newPeriod, oldPeriod) => {
  // Refresh similar houses if the booking period changed
  if (
    (!newPeriod && oldPeriod) ||
    (newPeriod &&
      (!oldPeriod ||
        newPeriod[0] !== oldPeriod[0] ||
        newPeriod[1] !== oldPeriod[1]))
  )
    await refresh()
})
</script>

<template>
  <div v-if="housesSimilar?.length">
    <div
      class="mb-4 flex flex-col gap-4 pl-4 sm:mb-6 sm:flex-row sm:items-center sm:pl-0"
    >
      <h2 class="m-0 text-2xl">
        {{ $t('house.similarHouses.title') }}
      </h2>
      <div class="flex items-center gap-4">
        <div
          v-if="booking.startDate && booking.endDate"
          class="flex items-center gap-2 rounded bg-secondary-100 px-2 py-1"
        >
          <BaseIcon name="calendar" :size="1.5" />
          <p class="m-0 text-md">
            {{ formatShortestPeriod([booking.startDate, booking.endDate]) }}
          </p>
        </div>
        <div class="flex items-center gap-2 rounded bg-secondary-100 px-2 py-1">
          <BaseIcon name="group" :size="1.5" />
          <p class="m-0 text-md">
            +{{ $t('house.similarHouses.guests', house.capacity) }}
          </p>
        </div>
      </div>
    </div>

    <VirtualBaseCarousel
      :navigation="housesSimilar.length > (isDesktopOrTablet ? 3 : 1)"
      :pagination="isDesktopOrTablet"
      :slides="housesSimilar"
      :swiper-options="swiperOptions"
      :virtual="false"
      class-name="similar-houses__slider"
    >
      <template #slider-content="{ slide, slideIndex }">
        <SearchHouseCard
          :id="Number(slide.id)"
          :key="slide.id"
          :bathrooms="slide.bathrooms"
          :bedrooms="slide.bedrooms"
          :capacity="slide.capacity"
          :class="[
            'my-1',
            { 'mx-4 !rounded-md border border-gray-200': isMobile },
          ]"
          :destination="slide.destination[locale]"
          :photos="slide.photos"
          :price="getPriceText({ t, locale }, slide, currency, bookingPeriod)"
          :slug="slide.slug[locale]"
          slug-name="house-slug"
          :title="slide.name"
          @handle-click="onCrossSellClick(slideIndex, slide)"
        >
          <template #card-media>
            <VirtualBaseCarousel
              class-name="house-card__carousel"
              class="!absolute left-0 top-0 size-full"
              loop
              navigation
              :pagination="isMobile"
              swiper-nav-el-class="swiper__photos"
              :slides="slide.photos"
            >
              <template #slider-content="{ slide: photo }">
                <div class="size-full">
                  <BaseNuxtImg
                    :alt="slide.name"
                    :src="photo"
                    class="rounded-t duration-500 ease-in-out group-hover:scale-105"
                    sizes="sm:100vw md:50vw lg:50vw xl:25vw xxl:25vw"
                  />
                </div>
              </template>
              <template #navigation>
                <div
                  class="swiper__photos--prev absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 opacity-0 duration-500 ease-in-out group-hover:opacity-100"
                  @click.stop.prevent
                >
                  <BaseIcon name="chevronLeft" />
                </div>
                <div
                  class="swiper__photos--next absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white p-2 opacity-0 duration-500 ease-in-out group-hover:opacity-100"
                  @click.stop.prevent
                >
                  <BaseIcon name="chevronRight" />
                </div>
                <VirtualBaseCarouselPaginationV1 />
              </template>
            </VirtualBaseCarousel>
          </template>

          <template #card-action>
            <WishlistToggle
              v-slot="{ active }"
              :house-id="slide.id"
              :house-name="slide.name"
              @callback-click="
                $emit('click-favorite', {
                  id: slide.id,
                  name: slide.name,
                  iconicCollection: slide.iconic_collection,
                  onlineReservation: slide.pre_booking_available,
                  publicPrice: slide.publicPrice,
                })
              "
            >
              <HouseWishlistButton :is-active="active" />
            </WishlistToggle>
          </template>

          <template #before-price>
            <div class="mt-3 flex min-h-7 gap-2">
              <span
                v-for="keyfeature in slide.highlight_key_features"
                :key="keyfeature.identifier"
                class="max-w-[50%] truncate rounded bg-gray-100 px-2 py-1 text-xs"
              >
                {{ keyfeature.name[locale] }}
              </span>
            </div>
          </template>
        </SearchHouseCard>
      </template>

      <template #navigation="{ position }">
        <VirtualBaseCarouselPaginationV2
          v-if="isDesktopOrTablet && housesSimilar.length > 3"
          navigation
        />

        <VirtualBaseCarouselPaginationV4
          v-else-if="housesSimilar.length"
          navigation
          pagination
        >
          {{ position }}/{{ housesSimilar.length }}
        </VirtualBaseCarouselPaginationV4>
      </template>
    </VirtualBaseCarousel>
  </div>
</template>

<style scoped>
.similar-houses__slider :deep(.swiper-wrapper) {
  @apply mb-5;
}
.similar-houses__slider :deep(> .swiper > .swiper-wrapper > .swiper-slide) {
  @apply sm:overflow-visible;
}
.similar-houses__slider :deep(h2) {
  @apply !mb-2;
}
</style>
